import {
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Icon,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { BrandButton } from "../BrandButton/BrandButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { FaCaretDown, FaCaretRight } from "react-icons/fa";
import Editor, { useMonaco } from "@monaco-editor/react";
import { fetchProcessVaraiblesbyProcessId } from "../../services/state/Process-modular/processModular-async-calls";
import { fetchRules } from "../../services/state/rules/rules-async-calls";

export const ExpressionEditorModal = ({
  isOpen,
  onClose,
  value,
  handleValue = () => {},
}: {
  isOpen: boolean;
  onClose: () => void;
  value?: string;
  handleValue?: (text: string) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const monaco = useMonaco();

  const [text, setText] = useState<string | undefined>(value);
  const selectedProcess = useSelector(
    (state: RootState) => state.processes.selectedProcess
  );
  const processVariables = useSelector(
    (state: RootState) => state.processModeler.processVariables
  );
  const rules = useSelector((state: RootState) => state.rules.rules);

  const [open, setOpen] = useState(false);

  const editorOptions = {
    selectOnLineNumbers: true,
    language: "groovy",
    theme: "custom-theme", // Apply custom theme
    minimap: { enabled: false },
    readOnly: false,
  };

  const ruleMatchers = rules.map(
    (rule) =>
      `rule!${rule.ruleNm.replace(/ /g, "_")}(${rule.ruleInputs
        .map((input) => input.paramNm)
        .join(", ")})`
  );

  const pvMatchers = processVariables.map(
    (pv) => `pv!${pv.varNm.replace(/ /g, "_")}`
  );

  const defineGroovyLanguage = (monacoInstance: any) => {
    if (!monacoInstance) return;

    // Register Groovy language
    monacoInstance.languages.register({ id: "groovy" });

    // Define the syntax highlighting rules using Monarch tokens
    monacoInstance.languages.setMonarchTokensProvider("groovy", {
      tokenizer: {
        root: [
          [
            /\b(def|class|if|else|while|for|return|import|package|public|private|protected|static|void|true|false|null)\b/,
            "keyword",
          ],
          [/pv![a-zA-Z_$][\w$]*(?:\s*\([^\)]*\))?/, "constant"],
          [/(rule![a-zA-Z_][a-zA-Z0-9_]*|exp!)(?=\s*\([^)]*\))/, "comment"],
          [/[{}()\[\]]/, "@brackets"],
          [/"/, "string", "@string"],
          [/\b\d+(\.\d+)?\b/, "number"],
          [/\/\*[\s\S]*?\*\//, "comment"],
          [/\/\/.*$/, "comment"],
        ],
        string: [
          [/[^\\"]+/, "string"],
          [/\\./, "string.escape"],
          [/"/, "string", "@pop"],
        ],
      },
    });

    // Define custom theme for Groovy syntax highlighting
    monacoInstance.editor.defineTheme("custom-theme", {
      base: "vs-dark",
      inherit: true,
      rules: [
        { token: "keyword", foreground: "569CD6" },
        { token: "constant", foreground: "4EC9B0", fontStyle: "italic" },
        { token: "comment", foreground: "6A9955", fontStyle: "italic" },
        { token: "string", foreground: "CE9178" },
        { token: "number", foreground: "B5CEA8" },
      ],
      colors: {
        "editor.foreground": "#D4D4D4",
        "editor.background": "#1E1E1E",
      },
    });

    const uniqueRuleMatchers = Array.from(new Set(ruleMatchers));
    const uniquePvMatchers = Array.from(new Set(pvMatchers));

    // Register completion provider for custom rule suggestion
    const provider = monacoInstance.languages.registerCompletionItemProvider(
      "groovy",
      {
        triggerCharacters: ["!", "(", " "],
        provideCompletionItems: (model: any, position: any) => {
          const textBeforeCursor = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: Math.max(1, position.column - 20),
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          // Suggest rule! unique matches
          if (textBeforeCursor.endsWith("rule!")) {
            return {
              suggestions: uniqueRuleMatchers.map((suggestion) => ({
                label: suggestion,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 5,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          // Suggest pv! unique matches
          if (textBeforeCursor.endsWith("pv!")) {
            return {
              suggestions: uniquePvMatchers.map((suggestion) => ({
                label: suggestion,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 3,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          return { suggestions: [] };
        },
      }
    );

    return () => {
      provider.dispose();
    };
  };

  useEffect(() => {
    if (monaco) {
      const dispose = defineGroovyLanguage(monaco);
      return dispose;
    }
  }, [monaco, ruleMatchers, pvMatchers]);

  const onSave = () => {
    if (text) {
      const parser = new DOMParser();
      const parsedHtml = parser.parseFromString(text, "text/html");
      const cleanText = parsedHtml.body.textContent || "";
      handleValue(cleanText);
    }
    onClose();
  };

  const appendVariableToEditor = (varName: string) => {
    const newValue = `${text ?? ""} pv!${varName}`;
    setText(newValue);
    handleValue(newValue);
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchProcessVaraiblesbyProcessId(selectedProcess?.uuid));
      dispatch(fetchRules(selectedProcess?.app.uuid as string));
      setText(value);
    }
  }, [isOpen, dispatch, selectedProcess, value]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl">
      <ModalOverlay />
      <ModalContent
        bg="rgba(0, 0, 0, 0.9)"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255, 255, 255, 0.4)"
        borderRadius="12px"
        color="white"
      >
        <ModalHeader fontSize="20px">Expression Editor</ModalHeader>
        <ModalCloseButton color="red" />
        <ModalBody>
          <Box height={"40vh"}>
            <HStack>
              <Box minW={"30%"} height={"38vh"}>
                <Tabs>
                  <TabList>
                    <Tab
                      color="white"
                      _selected={{
                        color: "white",
                        background:
                          "linear-gradient(to right, #7652f7, #9500ff)",
                        borderColor: "transparent",
                        borderRadius: "md",
                      }}
                    >
                      Data
                    </Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <HStack onClick={() => setOpen(!open)}>
                        <Text fontWeight={"bold"}>Process Variables</Text>
                        <Icon
                          as={
                            open
                              ? (FaCaretDown as React.ElementType)
                              : (FaCaretRight as React.ElementType)
                          }
                        />
                      </HStack>
                      {open && (
                        <Box ml={4} mt={2}>
                          <VStack align={"start"}>
                            {processVariables.map((pv) => (
                              <Text
                                key={pv.varNm}
                                size="sm"
                                color={"blue.500"}
                                variant="outline"
                                onClick={() => appendVariableToEditor(pv.varNm)}
                                cursor={"pointer"}
                              >
                                {pv.varNm}
                              </Text>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
              <Box
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                overflow="hidden"
                p={2}
                width={"100%"}
              >
                <Editor
                  value={text}
                  defaultLanguage="groovy"
                  theme="custom-theme"
                  height="25vh"
                  width="100%"
                  options={editorOptions}
                  onChange={(value) => setText(value || "")}
                />
              </Box>
            </HStack>
          </Box>
        </ModalBody>
        <ModalFooter gap={4}>
          {/* <BrandButton onClick={onClose}>Cancel</BrandButton> */}
          <BrandButton onClick={onSave}>Save</BrandButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
